import { useEffect, type RefObject } from "react";

import {
  normalizeWheelDeltaY,
  recordComposerScrollCollapseWheel,
  resetComposerScrollCollapse,
  type ComposerScrollCollapseState,
} from "./composerScrollCollapse";

/**
 * The wheel gesture over the timeline that rests the Composer, and the one
 * that brings it back.
 *
 * Listening on the document with capture rather than on the scroll node
 * itself. `ChatTimelinePanel` remounts the timeline on a thread change, and a
 * listener bound to the old scroll node would keep the gesture pointed at a
 * detached element for the rest of the session. Resolving the node per event
 * has no such lifetime to get wrong.
 *
 * A wheel that the timeline does not receive is not this gesture. That one
 * test also stands in for t3code's "ignore wheels over the editor": Forge's
 * Composer is the timeline's sibling, not its descendant, so a wheel over the
 * Draft can never be contained by the scroll node.
 */
/**
 * Let a suppressed gesture expire on wheels the timeline never sees.
 *
 * Without this the momentum tail of a suppressed gesture pauses the moment the
 * pointer leaves the timeline, and the next event over it reads as a fresh
 * gesture that may rest the Composer the person just typed into.
 */
function keepSuppressedGestureAlive(gesture: ComposerScrollCollapseState): void {
  if (!gesture.collapseSuppressed) return;
  recordComposerScrollCollapseWheel(gesture, {
    now: performance.now(),
    deltaY: 0,
    eligible: false,
  });
}

export function useComposerScrollCollapse(input: {
  /** False where the gesture has nothing to act on, such as a phone. */
  readonly enabled: boolean;
  /** Read at event time: whether the Composer is in a state that can rest. */
  readonly eligibleRef: RefObject<boolean>;
  /**
   * Shared with the focus tracking, which retires the running gesture when the
   * person starts typing. Two accumulators could not agree on what "the gesture
   * running now" means.
   */
  readonly gestureRef: RefObject<ComposerScrollCollapseState>;
  readonly getTimelineScrollNode: () => HTMLElement | null;
  readonly isTimelineAtEnd: () => boolean;
  readonly onScrollCollapse: () => void;
  readonly onTimelineReachedEnd: () => void;
}): void {
  const {
    enabled,
    eligibleRef,
    gestureRef,
    getTimelineScrollNode,
    isTimelineAtEnd,
    onScrollCollapse,
    onTimelineReachedEnd,
  } = input;
  useEffect(() => {
    if (!enabled) return;
    const gesture = gestureRef.current;

    const handleWheel = (event: WheelEvent) => {
      // A pinch zoom reports itself as a ctrl-wheel. It changes the scale, not
      // the reading position, so it is not a read back through the thread.
      if (event.ctrlKey || !(event.target instanceof Element)) return;
      const scrollNode = getTimelineScrollNode();
      if (!scrollNode?.contains(event.target)) {
        keepSuppressedGestureAlive(gesture);
        return;
      }

      const atEnd = isTimelineAtEnd();
      const deltaY = normalizeWheelDeltaY(event, scrollNode.clientHeight || window.innerHeight);
      // Arriving back at the newest turn returns the Composer, which is the
      // half of the gesture that gives the person their input back without
      // making them find a control.
      if (deltaY > 0 && atEnd) {
        onTimelineReachedEnd();
        resetComposerScrollCollapse(gesture);
        return;
      }
      const shouldRest = recordComposerScrollCollapseWheel(gesture, {
        now: performance.now(),
        deltaY,
        eligible: eligibleRef.current && scrollNode.scrollTop > 0,
      });
      if (shouldRest) onScrollCollapse();
    };

    document.addEventListener("wheel", handleWheel, { capture: true, passive: true });
    return () => {
      document.removeEventListener("wheel", handleWheel, true);
      resetComposerScrollCollapse(gesture);
    };
  }, [
    enabled,
    eligibleRef,
    gestureRef,
    getTimelineScrollNode,
    isTimelineAtEnd,
    onScrollCollapse,
    onTimelineReachedEnd,
  ]);
}
